import React from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import ArticleListItem from '../components/ArticleListItem';
import Button from '../components/Button';
import Card from '../components/Card';
import SEO from '../components/SEO';
import content from '../data/content.json';

const Home: React.FC = () => {
  const homepage = content.homepage;
  const introSection = homepage.content.find(sec => sec.section === 'Intro');
  const introText = introSection?.text ? introSection.text.split('\n\n')[1] || '' : '';

  const practiceAreasSection = homepage.content.find(sec => sec.section === 'Practice Areas');
  const practiceAreas = practiceAreasSection?.areas || [];

  const teaser = homepage.content.find(sec => sec.section === 'Lawyers Teaser');
  const teaserText = teaser?.text ? teaser.text.split('\n\n')[1] || '' : '';
  const teaserQuote = teaser?.quote ? teaser.quote.replace(/^> /, '') : '';

  const articlesSection = homepage.content.find(sec => sec.section === 'Recent Articles');
  const articles = articlesSection?.articles || [];

  // Map article slugs to their images
  const articleImages: { [key: string]: string } = {
    'kjelleretasje-kan-vaere-uegnet-for-boligformal': '/privateImages/kjeller01.webp',
    'rad-dersom-sameiet-vurderer-salg-av-raloft': '/privateImages/loft.webp',
  };

  const mappedArticles = articles.map(art => {
    const titleText = art.title.replace(/^#+\s*/, '');
    const listed = content.articles.listed_on_homepage.find(l =>
      l.title === titleText || l.title.includes(titleText)
    );
    const slug = listed?.slug;
    return {
      ...art,
      slug,
      date: listed?.date || art.date,
      title: titleText,
      image: slug ? articleImages[slug] : undefined,
    };
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LegalService",
    "name": "Advokatfirmaet Bruland",
    "description": "Advokatfirmaet i Oslo med spisskompetanse innen arv og skifte, arbeidsrett og fast eiendom. Erfarne advokater som gir målrettet juridisk bistand.",
    "url": "https://bruland.no",
    "telephone": "+4722535322",
    "email": "advokat@bruland.no",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Drammensveien 82c",
      "postalCode": "0271",
      "addressLocality": "Oslo",
      "addressCountry": "NO"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "59.9136930",
      "longitude": "10.7021640"
    },
    "sameAs": [
      "https://maps.google.com/?cid=18195599217391118543"
    ],
    "openingHours": "Mo-Fr 08:00-16:00",
    "priceRange": "$$",
    "areaServed": {
      "@type": "Country",
      "name": "Norway"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Juridiske tjenester",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Arv og Skifte",
            "description": "Juridisk bistand ved arv, skifte og testament"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Arbeidsrett",
            "description": "Juridisk bistand i arbeidstvister og arbeidsrettslige spørsmål"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Fast Eiendom",
            "description": "Juridisk bistand ved eiendomskjøp, eiendomsrett og tvister"
          }
        }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Advokat Oslo - Arv, Arbeidsrett, Eiendom"
        description="Advokatfirmaet i Oslo med spisskompetanse innen arv og skifte, arbeidsrett og fast eiendom. Erfarne advokater som gir målrettet juridisk bistand."
        keywords="advokat Oslo, advokatfirma, arv og skifte, arbeidsrett, fast eiendom, juridisk bistand, advokat, testament, eiendomsrett"
        canonicalUrl="/"
        structuredData={structuredData}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary via-primary-dark to-primary-light text-white min-h-[500px] flex items-center">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Text Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-5xl md:text-6xl font-bold mb-6 font-serif text-white">
                Målrettet juridisk bistand
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-white/90">
                {introText}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/kontakt">
                  <Button size="lg">Kontakt oss</Button>
                </Link>
                <Link to="/om-oss">
                  <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary">
                    Les mer
                  </Button>
                </Link>
              </div>
            </div>

            {/* Image */}
            <div className="hidden lg:block">
              <img
                src="/privateImages/drammensveien2bilde.webp"
                alt="Advokatfirmaet Bruland kontor"
                className="rounded-lg shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Practice Areas Section */}
      <Section title="Fagområder" background="gray" spacing="lg" centered>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
          {practiceAreas.map((area, idx) => {
            const icons = ['⚖️', '💼', '🏠'];
            const title = area.title.replace(/^#+\s*/, '');

            // Map titles to slugs
            const slugMap: { [key: string]: string } = {
              'Arv og Skifte': 'arv-og-skifte',
              'Arbeidsrett': 'arbeidsrett',
              'Fast Eiendom': 'fast-eiendom',
            };

            const slug = slugMap[title];

            // Practice areas with a dedicated landing page link there; others go to the fagområder anchor.
            const routeMap: { [key: string]: string } = {
              'Arv og Skifte': '/arv-og-testament',
              'Arbeidsrett': '/arbeidsrett',
              'Fast Eiendom': '/fast-eiendom',
            };
            const to = routeMap[title] || `/fagomrader#${slug}`;

            return (
              <Link key={idx} to={to} className="h-full">
                <Card hoverable>
                  <div className="flex flex-col h-full">
                    {/* Icon Placeholder */}
                    <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">{icons[idx] || '📄'}</span>
                    </div>

                    {/* Title */}
                    <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif text-center">
                      {title}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 mb-4 text-center">
                      {area.description}
                    </p>

                    {/* Link */}
                    <div className="text-center mt-auto">
                      <span className="text-primary hover:text-primary-dark hover:underline transition-colors font-medium">
                        {area.link_text}
                      </span>
                    </div>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      </Section>

      {/* Lawyer Teaser Section */}
      <Section title="Advokater med erfaring" background="white" spacing="lg" container="narrow" centered>
        {/* Quote */}
        <blockquote className="text-3xl font-serif italic text-primary my-12 border-l-4 border-accent pl-6">
          "{teaserQuote}"
        </blockquote>

        {/* Text */}
        <p className="text-lg text-gray-600 mb-8">
          {teaserText}
        </p>

        {/* CTA */}
        <Link to="/om-oss">
          <Button size="lg">{teaser?.link_text || 'Bli kjent med oss'}</Button>
        </Link>
      </Section>

      {/* Recent Articles Section */}
      <Section title="Nylige artikler" background="gray" spacing="lg">
        <div className="space-y-6 mt-8">
          {mappedArticles.map((art, idx) => (
            <ArticleListItem
              key={idx}
              title={art.title}
              slug={art.slug}
              linkText={art.link_text}
              image={art.image}
            />
          ))}
        </div>
      </Section>

      {/* Bottom CTA Section */}
      <section className="bg-primary text-white text-center py-20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold mb-8 font-serif text-white">
            Trenger du juridiske råd?
          </h2>
          <Link to="/kontakt">
            <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary">
              Forespør en samtale →
            </Button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default Home;
